import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../Utils/Axios';
import UrduKeyboard from '../../Components/UrduKeyboard';

function BillsTab({ bills, companyLanguage, navigate, handleEditBill, handleDeleteBill, handleViewBill }) {
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchUrduKeyboard, setShowSearchUrduKeyboard] = useState(false);
  const searchInputRef = useRef(null);
  // Set today's date (local) as default for date filters
  const today = new Date().toLocaleDateString('en-CA');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterAmountMin, setFilterAmountMin] = useState('');
  const [filterAmountMax, setFilterAmountMax] = useState('');
  const [showFilters, setShowFilters] = useState(true); // Auto-open filters
  const [selectedBills, setSelectedBills] = useState([]);

  // Get unique suppliers for filter dropdown (customerName stores supplier name in bills)
  const uniqueSuppliers = useMemo(() => {
    const suppliers = new Set();
    bills.forEach(bill => {
      const name = bill.customerNameUrdu || bill.customerName;
      if (name) suppliers.add(name);
    });
    return Array.from(suppliers).sort();
  }, [bills]);

  // Calculate پختہ بکری (Net Sale) for a bill - same logic as BillView
  const calculateNetSale = (bill) => {
    // Get raw sale (خام بکری)
    const rawSale = bill.sales?.rawSale || bill.sales?.['Raw Sale'] || bill.expenses?.['خام بکری'] || bill.rawSale || 0;
    const rawSaleNum = typeof rawSale === 'string' ? parseFloat(rawSale) || 0 : (rawSale || 0);

    // Calculate total expenses - same logic as BillView (exclude non-expense fields and laga)
    let totalExpenses = 0;
    if (bill.expenses && typeof bill.expenses === 'object') {
      Object.keys(bill.expenses).forEach(key => {
        const value = bill.expenses[key];
        // Skip non-expense fields: Vehicle, ItemName, ItemUnit, ItemQuantity, ActualVehicle, calculated fields, and laga
        const isNonExpenseField =
          key === 'Vehicle' ||
          key === 'ActualVehicle' ||
          key === 'ItemName' ||
          key === 'ItemUnit' ||
          key === 'ItemQuantity' ||
          key === 'خام بکری' ||
          key === 'اخراجات' ||
          key === 'پختہ بکری' ||
          key === 'لاگا' || // Exclude laga from expenses (it goes to amdani only)
          key === 'Laga' ||
          key === 'DeliveryMethod' ||
          key === 'GoodsMessageUrdu';

        if (!isNonExpenseField) {
          const numValue = typeof value === 'string' ? parseFloat(value) || 0 : (typeof value === 'number' && !isNaN(value) ? value : 0);
          totalExpenses += numValue;
        }
      });
    }

    // Calculate net sale: rawSale - totalExpenses
    return rawSaleNum - totalExpenses;
  };

  // Filter bills based on all filter criteria
  const filteredBills = useMemo(() => {
    return bills.filter(bill => {
      // Search term filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const customerName = (bill.customerNameUrdu || bill.customerName || '').toLowerCase();
        const serialNumber = (bill.serialNumber || '').toString().toLowerCase();
        const voucherNumber = (bill.voucherNumber || '').toString().toLowerCase();

        if (!customerName.includes(searchLower) &&
          !serialNumber.includes(searchLower) &&
          !voucherNumber.includes(searchLower)) {
          return false;
        }
      }

      // Date range filter
      if (filterDateFrom && bill.date) {
        const billDate = new Date(bill.date);
        const fromDate = new Date(filterDateFrom);
        billDate.setHours(0, 0, 0, 0);
        fromDate.setHours(0, 0, 0, 0);
        if (billDate < fromDate) return false;
      }
      if (filterDateTo && bill.date) {
        const billDate = new Date(bill.date);
        const toDate = new Date(filterDateTo);
        billDate.setHours(0, 0, 0, 0);
        toDate.setHours(0, 0, 0, 0);
        if (billDate > toDate) return false;
      }

      // Supplier filter (customerName stores supplier name in bills)
      if (filterCustomer) {
        const supplierName = bill.customerNameUrdu || bill.customerName || '';
        if (supplierName !== filterCustomer) return false;
      }

      // Amount range filter (using calculated netSale)
      const amount = calculateNetSale(bill);
      if (filterAmountMin) {
        if (amount < parseFloat(filterAmountMin)) return false;
      }
      if (filterAmountMax) {
        if (amount > parseFloat(filterAmountMax)) return false;
      }

      return true;
    });
  }, [bills, searchTerm, filterDateFrom, filterDateTo, filterCustomer, filterAmountMin, filterAmountMax]);

  // Calculate total پختہ بکری (Net Sale) for all filtered bills
  const totalPukhtaBakri = useMemo(() => {
    return filteredBills.reduce((sum, bill) => {
      return sum + calculateNetSale(bill);
    }, 0);
  }, [filteredBills]);

  // Handle Urdu keyboard input for search field
  const handleSearchUrduKeyPress = (key) => {
    if (key === 'backspace') {
      setSearchTerm((prev) => prev ? prev.slice(0, -1) : '');
    } else {
      setSearchTerm((prev) => (prev || '') + key);
    }
  };

  // Toggle Urdu keyboard on search input click
  const handleSearchInputClick = () => {
    setShowSearchUrduKeyboard(prev => !prev);
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterCustomer('');
    setFilterAmountMin('');
    setFilterAmountMax('');
  };

  // Handle auto-filter from URL search parameters (e.g., from Day Book)
  useEffect(() => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      setFilterDateFrom(dateParam);
      setFilterDateTo(dateParam);
      setShowFilters(true);
    }
  }, [searchParams]);

  // Check if any filter is active
  const hasActiveFilters = searchTerm || filterDateFrom || filterDateTo || filterCustomer || filterAmountMin || filterAmountMax;

  // Select All functionality
  const handleSelectAll = () => {
    const allFilteredIds = filteredBills.map(bill => bill._id);
    const allSelected = allFilteredIds.every(id => selectedBills.includes(id));

    if (allSelected) {
      // Deselect all filtered bills
      setSelectedBills(prev => prev.filter(id => !allFilteredIds.includes(id)));
    } else {
      // Select all filtered bills
      setSelectedBills(prev => {
        const newSelection = [...prev];
        allFilteredIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    }
  };

  const handleToggleBill = (billId) => {
    setSelectedBills(prev => {
      if (prev.includes(billId)) {
        return prev.filter(id => id !== billId);
      } else {
        return [...prev, billId];
      }
    });
  };

  const isAllSelected = filteredBills.length > 0 && filteredBills.every(bill => selectedBills.includes(bill._id));

  // Bulk Delete functionality
  const handleBulkDelete = async () => {
    if (selectedBills.length === 0) return;

    const confirmMessage = companyLanguage === 'ur'
      ? `کیا آپ واقعی ${selectedBills.length} بلز ختم کرنا چاہتے ہیں؟`
      : `Are you sure you want to delete ${selectedBills.length} bills?`;

    if (!window.confirm(confirmMessage)) return;

    try {
      const token = localStorage.getItem('companyToken');
      if (!token) {
        toast.error(companyLanguage === 'ur' ? 'لاگ ان کریں' : 'Please login');
        return;
      }

      let successCount = 0;
      let failCount = 0;

      // Delete all selected bills in parallel
      const deletePromises = selectedBills.map(async (billId) => {
        try {
          await api.delete(`/bill/company/${billId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          return { success: true, billId };
        } catch (error) {
          console.error(`Failed to delete bill ${billId}:`, error);
          return { success: false, billId };
        }
      });

      const results = await Promise.all(deletePromises);
      successCount = results.filter(r => r.success).length;
      failCount = results.filter(r => !r.success).length;

      setSelectedBills([]);

      if (successCount > 0) {
        toast.success(
          companyLanguage === 'ur'
            ? `${successCount} بلز کامیابی سے ختم ہو گئے`
            : `${successCount} bills deleted successfully`
        );
        // Refresh bills list by calling the parent's fetch function if available
        if (window.location.reload) {
          setTimeout(() => window.location.reload(), 500);
        }
      }

      if (failCount > 0) {
        toast.error(
          companyLanguage === 'ur'
            ? `${failCount} بلز ختم کرنے میں ناکام`
            : `Failed to delete ${failCount} bills`
        );
      }
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error(companyLanguage === 'ur' ? 'ختم کرنے میں ناکام' : 'Failed to delete bills');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 border border-brand-card animate-fade-in-up">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <h2 className="text-2xl font-bold text-gray-900">
          {companyLanguage === 'ur' ? 'بل' : companyLanguage === 'both' ? 'Bills / بل' : 'My Bills'}
          <span className="text-lg text-gray-500 ml-2">
            ({filteredBills.length}/{bills.length})
          </span>
        </h2>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 transform hover:scale-105 ${showFilters || hasActiveFilters
              ? 'bg-green-500 hover:bg-green-600 text-white'
              : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {companyLanguage === 'ur' ? 'فلٹر' : 'Filter'}
            {hasActiveFilters && (
              <span className="bg-white text-green-500 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                !
              </span>
            )}
          </button>
          <button
            onClick={() => navigate('/bill/create-part2')}
            className="bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 rounded-lg transition flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {companyLanguage === 'ur' ? 'نیا بل' : 'Create New Bill'}
          </button>
        </div>
      </div>

      {/* Filter Section */}
      {showFilters && (
        <div className="mb-2 p-1.5 bg-gradient-to-r from-green-50 to-lime-50 rounded-xl border border-green-200 animate-fade-in-down shadow-lg">
          <div className="flex justify-between items-center mb-2">
            <h3 className={`font-bold text-green-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
              {companyLanguage === 'ur' ? 'فلٹرز' : 'Filters'}
            </h3>
            <div className="flex gap-2">
              {hasActiveFilters && (
                <button
                  onClick={resetFilters}
                  className="px-2 py-1 bg-gray-500 hover:bg-gray-600 text-white text-xs rounded-lg transition-all"
                >
                  {companyLanguage === 'ur' ? 'ری سیٹ' : 'Reset'}
                </button>
              )}
              <button
                onClick={() => setShowFilters(false)}
                className="px-2 py-1 bg-gray-300 hover:bg-gray-400 text-gray-700 text-xs rounded-lg transition-all"
              >
                {companyLanguage === 'ur' ? 'بند کریں' : 'Close'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-1.5">
            {/* Search */}
            <div>
              <label className={`block text-xs font-medium text-gray-700 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {companyLanguage === 'ur' ? 'تلاش:' : 'Search:'}
              </label>
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={handleSearchInputClick}
                placeholder={companyLanguage === 'ur' ? 'کسٹمر، نمبر، واؤچر...' : 'Customer, Number, Voucher...'}
                className={`w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs ${companyLanguage === 'ur' ? 'font-urdu text-right' : ''}`}
                style={companyLanguage === 'ur' ? {
                  fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif",
                  lineHeight: '1.8',
                  paddingTop: '6px',
                  paddingBottom: '6px',
                  fontSize: '12px',
                  height: 'auto',
                  minHeight: '32px',
                  display: 'block',
                  overflow: 'visible',
                  verticalAlign: 'middle'
                } : {}}
                dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}
                lang={companyLanguage === 'ur' ? 'ur' : 'en'}
              />
            </div>

            {/* Date From */}
            <div>
              <label className={`block text-xs font-medium text-gray-700 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {companyLanguage === 'ur' ? 'تاریخ سے:' : 'Date From:'}
              </label>
              <input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                className="w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs"
              />
            </div>

            {/* Date To */}
            <div>
              <label className={`block text-xs font-medium text-gray-700 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {companyLanguage === 'ur' ? 'تاریخ تک:' : 'Date To:'}
              </label>
              <input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                className="w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs"
              />
            </div>

            {/* Supplier Filter */}
            <div>
              <label className={`block text-xs font-medium text-gray-700 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {companyLanguage === 'ur' ? 'سپلائر:' : 'Supplier:'}
              </label>
              <select
                value={filterCustomer}
                onChange={(e) => setFilterCustomer(e.target.value)}
                className="w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs"
              >
                <option value="">{companyLanguage === 'ur' ? 'تمام' : 'All'}</option>
                {uniqueSuppliers.map(supplier => (
                  <option key={supplier} value={supplier}>{supplier}</option>
                ))}
              </select>
            </div>

          </div>
        </div>
      )}

      {bills.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>{companyLanguage === 'ur' ? 'ابھی کوئی بل نہیں' : 'No bills found yet'}</p>
          <p className="text-sm mt-2">
            {companyLanguage === 'ur' ? 'نیا بل بنانے کے لیے اوپر بٹن دبائیں' : 'Use the button above to create a new bill'}
          </p>
        </div>
      ) : filteredBills.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>{companyLanguage === 'ur' ? 'فلٹر کے مطابق کوئی بل نہیں ملا' : 'No bills match the filters'}</p>
          <button
            onClick={resetFilters}
            className="mt-4 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-all"
          >
            {companyLanguage === 'ur' ? 'فلٹرز ری سیٹ کریں' : 'Reset Filters'}
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Header Row */}
          {filteredBills.length > 0 && (
            <div className="bg-gray-100 border border-brand-card rounded-lg py-2 px-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    className="w-5 h-5 text-brand-header border-gray-300 rounded focus:ring-brand-header cursor-pointer"
                  />
                  <span className={`text-sm font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                    {companyLanguage === 'ur' ? 'سب منتخب' : 'Select All'}
                  </span>
                  {selectedBills.length > 0 && (
                    <span className={`text-sm text-gray-600 ml-2 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                      ({selectedBills.length} {companyLanguage === 'ur' ? 'منتخب' : 'selected'})
                    </span>
                  )}
                </div>
                {selectedBills.length > 0 && (
                  <button
                    onClick={handleBulkDelete}
                    className={`px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-all flex items-center gap-2 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    {companyLanguage === 'ur' ? `ختم کریں (${selectedBills.length})` : `Delete (${selectedBills.length})`}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-6 gap-4 items-center" dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
                <div className={`text-sm font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {companyLanguage === 'ur' ? 'منتخب' : 'Select'}
                </div>
                <div className={`text-sm font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {companyLanguage === 'ur' ? 'بل' : 'Bill'}
                </div>
                <div className={`text-sm font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {companyLanguage === 'ur' ? 'تاریخ' : 'Date'}
                </div>
                <div className={`text-sm font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {companyLanguage === 'ur' ? 'سپلائر' : 'Supplier'}
                </div>
                <div className={`text-sm font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {companyLanguage === 'ur' ? 'پختہ بکری' : 'Net Sale'}
                </div>
                <div className={`text-sm font-bold text-gray-700 text-center`}>
                  {companyLanguage === 'ur' ? 'عمل' : 'Actions'}
                </div>
              </div>
            </div>
          )}

          {/* Bill Items */}
          {filteredBills.map((bill) => {
            const isSelected = selectedBills.includes(bill._id);
            return (
              <div
                key={bill._id}
                className={`border border-brand-card rounded-lg py-2 px-3 hover:shadow-md transition-all ${isSelected ? 'bg-emerald-50 border-green-400' : 'bg-white'
                  }`}
              >
                <div className="grid grid-cols-6 gap-4 items-center" dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggleBill(bill._id)}
                      className="w-5 h-5 text-brand-header border-gray-300 rounded focus:ring-brand-header cursor-pointer"
                    />
                  </div>
                  <div className={`font-semibold text-gray-900 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                    {companyLanguage === 'ur' ? 'بل #' : 'Bill #'}{bill.serialNumber || bill._id.slice(-6)}
                  </div>
                  <div className={`text-sm text-gray-600 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                    {new Date(bill.date).toLocaleDateString()}
                  </div>
                  <div className={`text-sm text-gray-600 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                    {bill.customerNameUrdu || bill.customerName || 'N/A'}
                  </div>
                  <div className={`text-sm font-semibold text-blue-600 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                    Rs. {calculateNetSale(bill).toLocaleString()}
                  </div>
                  <div className="flex gap-2 justify-center">
                    <button
                      onClick={() => handleViewBill(bill._id)}
                      className={`px-2 py-1 bg-brand-header hover:bg-emerald-800 text-white text-xs rounded transition-all ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                    >
                      {companyLanguage === 'ur' ? 'دیکھیں' : 'View'}
                    </button>
                    <button
                      onClick={() => handleEditBill(bill)}
                      className={`px-2 py-1 bg-yellow-600 hover:bg-yellow-700 text-white text-xs rounded transition-all ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                    >
                      {companyLanguage === 'ur' ? 'تبدیل کریں' : 'Edit'}
                    </button>
                    <button
                      onClick={() => handleDeleteBill(bill._id)}
                      className={`px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-all ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                    >
                      {companyLanguage === 'ur' ? 'ختم کریں' : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Total Row */}
          {filteredBills.length > 0 && (
            <div className="bg-emerald-100 border-2 border-emerald-400 rounded-lg py-3 px-3 mt-2">
              <div className="grid grid-cols-6 gap-4 items-center" dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
                <div className={`text-sm font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {companyLanguage === 'ur' ? 'کل' : 'Total'}
                </div>
                <div className={`text-sm font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {/* Empty for Bill column */}
                </div>
                <div className={`text-sm font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {/* Empty for Date column */}
                </div>
                <div className={`text-sm font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {/* Empty for Supplier column */}
                </div>
                <div className={`text-lg font-bold text-emerald-700 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  Rs. {totalPukhtaBakri.toLocaleString()}
                </div>
                <div className="text-center">
                  {/* Empty for Actions column */}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      <UrduKeyboard
        isVisible={showSearchUrduKeyboard}
        onKeyPress={handleSearchUrduKeyPress}
        onClose={() => setShowSearchUrduKeyboard(false)}
      />
    </div>
  );
}

export default BillsTab;


